import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface EquityCurveLogProps {
  /** Daily returns series */
  returns: number[];
  /** Start date */
  startDate: string;
  /** Optional benchmark returns for comparison */
  benchmarkReturns?: number[];
  /** Chart height */
  height?: number;
}

/**
 * Log-scale equity curve showing cumulative returns.
 * Uses log scale on Y-axis to properly visualize compounding.
 */
export default function EquityCurveLog({
  returns,
  startDate,
  benchmarkReturns,
  height = 300
}: EquityCurveLogProps) {
  const data = useMemo(() => {
    let cumulative = 1;
    let benchCumulative = 1;
    const start = new Date(startDate);

    return returns.map((ret, i) => {
      cumulative *= (1 + ret);
      if (benchmarkReturns && benchmarkReturns[i] !== undefined) {
        benchCumulative *= (1 + benchmarkReturns[i]);
      }

      const date = new Date(start);
      date.setDate(date.getDate() + i);

      return {
        date: date.toISOString().split('T')[0],
        strategy: cumulative,
        strategyReturn: (cumulative - 1) * 100,
        benchmark: benchmarkReturns ? benchCumulative : undefined,
        benchmarkReturn: benchmarkReturns ? (benchCumulative - 1) * 100 : undefined,
      };
    });
  }, [returns, startDate, benchmarkReturns]);

  const formatYAxis = (value: number) => {
    return `${((value - 1) * 100).toFixed(0)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;

    return (
      <div className="bg-gray-800 border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-400 text-xs mb-2">{label}</p>
        {payload.map((entry: any, i: number) => (
          <p key={i} className="text-sm" style={{ color: entry.color }}>
            {entry.name === 'strategy' ? 'Strategy' : 'Benchmark'}:
            <span className="ml-2 font-medium tabular-nums">
              {((entry.value - 1) * 100).toFixed(2)}%
            </span>
          </p>
        ))}
      </div>
    );
  };

  // Calculate domain for log scale
  const minValue = Math.min(...data.map(d => Math.min(d.strategy, d.benchmark || d.strategy)));
  const maxValue = Math.max(...data.map(d => Math.max(d.strategy, d.benchmark || d.strategy)));

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-white">Equity Curve (Log Scale)</h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-profit"></div>
            <span className="text-gray-400">Strategy</span>
          </div>
          {benchmarkReturns && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-gray-500"></div>
              <span className="text-gray-400">Benchmark</span>
            </div>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="date"
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
            }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
            tickFormatter={formatYAxis}
            scale="log"
            domain={[Math.max(0.1, minValue * 0.95), maxValue * 1.05]}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={1} stroke="#6B7280" strokeDasharray="3 3" />
          {benchmarkReturns && (
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke="#6B7280"
              strokeWidth={1}
              dot={false}
              name="benchmark"
            />
          )}
          <Line
            type="monotone"
            dataKey="strategy"
            stroke="#005AB5"
            strokeWidth={2}
            dot={false}
            name="strategy"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Summary stats */}
      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-between text-sm">
        <div className="text-gray-400">
          Final Value: <span className="text-white font-medium tabular-nums">
            {((data[data.length - 1]?.strategy || 1) * 100 - 100).toFixed(2)}%
          </span>
        </div>
        {benchmarkReturns && (
          <div className="text-gray-400">
            vs Benchmark: <span className={`font-medium tabular-nums ${
              (data[data.length - 1]?.strategy || 1) > (data[data.length - 1]?.benchmark || 1)
                ? 'text-profit' : 'text-loss'
            }`}>
              {(((data[data.length - 1]?.strategy || 1) - (data[data.length - 1]?.benchmark || 1)) * 100).toFixed(2)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
